import OpenAI from "openai";
import { getContext } from "./contextBuffer";
import { REFERENCE_IMAGES } from "./referenceImages";
import { matchWordbank, matchSmells } from "./CAT_WORDBANK";

const openai = new OpenAI({
  apiKey: process.env.REACT_APP_OPENAI_API_KEY,
  dangerouslyAllowBrowser: true,
});

// ── VOICE: Fill inspection form from transcript ───────────────────────────
// Supports both Wheel Loader (WL) and Hydraulic Excavator (HEX) forms
export async function fillInspectionForm(machineClass = "wheel loader") {
  const context = getContext();
  if (!context.trim()) return null;

  const isExcavator = machineClass === "excavator";

  const fieldDefs = isExcavator ? `
EXCAVATOR INSPECTION FIELDS (CAT HEX Safety & Maintenance Inspection PDFs — 300.9-308, 312-318, 320-352, 365-5230):
FROM THE GROUND:
- G.1: Bucket, GET (excessive wear, damage, cracks)
- G.2: Bucket Cylinder & Linkage (wear, damage, leaks, lubricate)
- G.3: Stick & Cylinder (wear, damage, leaks, lubricate)
- G.4: Boom & Cylinders (wear, damage, leaks, lubricate)
- G.5: Undercarriage (wear, damage, tension)
- G.6: Carbody (cracks, damage)
- G.7: Final Drive / Swing Drive (leaks, damage)
- G.8: Steps & Handholds (condition, cleanliness)
ENGINE COMPARTMENT / LOWER PLATFORM:
- E.1: Engine Coolant (fluid level)
- E.2: Engine Oil (fluid level)
- E.3: Hydraulic Oil Tank (fluid level, damage, leaks)
- E.4: Radiator (fin blockage, leaks)
- E.5: All Hoses (cracks, wear spots, leaks)
- E.6: All Belts (tightness, wear, cracks)
- E.7: Air Filter (restriction indicator)
- E.8: Fuel Tank (fuel level, damage, leaks)
- E.9: DEF Tank (fluid level, debris buildup)
INSIDE THE CAB:
- C.1: Seat & Seatbelt (adjustment, wear, install date — replace after 3 years)
- C.2: Horn, Travel Alarm, Lights (proper function)
- C.3: Travel Controls (correct operation, free from debris)
- C.4: ROPS (damage, loose mounting bolts)
- C.5: Heating & Cooling System (proper function)` : `
WHEEL LOADER INSPECTION FIELDS:
- 1.1: Tires and Rims
- 1.2: Bucket Cutting Edge, Tips, or Moldboard
- 1.3: Bucket Tilt Cylinders and Hoses
- 1.7: Transmission and Transfer Gears
- 1.8: Differential and Final Drive Oil
- 2.1: Engine Oil Level
- 2.2: Engine Coolant Level
- 2.3: Radiator Cores for Debris
- 4.1: Seat & Seatbelt
- 4.4: Backup Alarm`;

  const response = await openai.chat.completions.create({
    model: "gpt-4o",
    max_tokens: 1200,
    messages: [
      {
        role: "system",
        content: `You are a CAT heavy equipment inspection AI assistant.
Extract inspection data from technician voice notes and return ONLY valid JSON, no markdown, no explanation.
${fieldDefs}

Return this exact format:
{
  "fields": [
    { "id": "G.5", "status": "FAIL", "comment": "Undercarriage shows excessive wear on track links", "confidence": 91 }
  ]
}

Status must be: PASS, MONITOR, or FAIL only. Only include fields that were clearly mentioned.`,
      },
      { role: "user", content: `Technician transcript:\n\n${context}` },
    ],
    response_format: { type: "json_object" },
  });

  try { return JSON.parse(response.choices[0].message.content); }
  catch { return null; }
}

// ── VOICE: Summarize a single transcript chunk ────────────────────────────
export async function summarizeChunk(text) {
  const response = await openai.chat.completions.create({
    model: "gpt-4o-mini",
    max_tokens: 60,
    messages: [
      {
        role: "system",
        content: `You are a CAT heavy equipment inspection assistant.
If the text contains relevant equipment inspection content, summarize in ONE phrase max 8 words.
Start with ⚠ for issue/damage, ✓ for good/pass, ~ for monitor/watch.
Use CAT terms: transmission, coolant, hydraulic, differential, radiator, tires, bucket, boom, stick, undercarriage, swing drive, ROPS, hose, belt, DEF.
If the text has NO relevant equipment content (background noise, unrelated speech, etc), return exactly: null
Return ONLY the summary phrase or the word null. Nothing else.`,
      },
      { role: "user", content: text },
    ],
  });
  const raw = response.choices[0].message.content.trim();
  if (!raw || raw === "null" || raw.toLowerCase() === "null") return null;
  return raw;
}

// ── VOICE: Generate session summary for Fleet tab ─────────────────────────
// Called in Fleet tab to show a professional summary of the transcript
export async function generateSessionSummary(allChunks, fields) {
  if (!allChunks || allChunks.length === 0) return null;
  const transcript = allChunks.map(c => `[${c.time}] ${c.text}`).join("\n");
  const failFields = fields.filter(f => f.status === "FAIL").map(f => f.label).join(", ");
  const monFields = fields.filter(f => f.status === "MONITOR").map(f => f.label).join(", ");
  const passFields = fields.filter(f => f.status === "PASS").map(f => f.label).join(", ");

  try {
    const response = await openai.chat.completions.create({
      model: "gpt-4o-mini",
      max_tokens: 100,
      messages: [
        {
          role: "system",
          content: `You are a CAT equipment inspection AI.
Write a 1-2 sentence professional inspection summary. Be direct and use CAT terminology. Max 30 words.`,
        },
        {
          role: "user",
          content: `TRANSCRIPT:\n${transcript.substring(0, 800)}\n\nFAIL: ${failFields || "none"}\nMONITOR: ${monFields || "none"}\nPASS: ${passFields || "none"}`,
        },
      ],
    });
    return response.choices[0].message.content.trim();
  } catch {
    const parts = [];
    if (failFields) parts.push(`FAIL: ${failFields}`);
    if (monFields) parts.push(`MON: ${monFields}`);
    if (passFields) parts.push(`PASS: ${passFields}`);
    return parts.join(" · ") || "Inspection completed";
  }
}

// ── REAL-TIME SMOKE / SMELL DETECTION via LLM ─────────────────────────────
// Analyzes transcript text for smoke/smell indicators.
//
// HARDWARE SENSOR INTEGRATION (future):
// - Web Bluetooth API: BLE gas sensors (MQ-2, MQ-135, SGP30)
//   navigator.bluetooth.requestDevice({ filters: [{ services: ['environmental_sensing'] }] })
// - WebUSB: USB VOC/smoke sensors
// - TensorFlow.js: Run on-device smoke detection ML model in browser
// - IoT bridge: POST sensor readings to this function via WebSocket
//
// The sensorData param accepts: { co: number (ppm), voc: number (ppb), smoke: boolean }
export async function detectSmokeFromLLM(text, sensorData = null) {
  if (!text && !sensorData) return [];

  const sensorContext = sensorData
    ? `\nSENSOR DATA: CO=${sensorData.co}ppm, VOC=${sensorData.voc}ppb, Smoke=${sensorData.smoke ? "DETECTED" : "clear"}`
    : "";

  try {
    const response = await openai.chat.completions.create({
      model: "gpt-4o-mini",
      max_tokens: 80,
      messages: [
        {
          role: "system",
          content: `You are a CAT equipment safety AI. Detect smoke or odor indicators from text.
Return ONLY a JSON array of detected smell IDs from: burning, blue_smoke, white_smoke, black_smoke, diesel, sweet, oil, sulfur, rubber, hot_metal
Return [] if none. Return ONLY the JSON array, nothing else.`,
        },
        { role: "user", content: `TEXT: ${text}${sensorContext}` },
      ],
    });
    const raw = response.choices[0].message.content.trim();
    try { return JSON.parse(raw); } catch { return []; }
  } catch { return []; }
}

// ── PHOTO: Few-shot vision classification ────────────────────────────────
export async function analyzePhoto(imageBase64) {
  const examples = [
    REFERENCE_IMAGES.find(r => r.status === "PASS"),
    REFERENCE_IMAGES.find(r => r.status === "MONITOR"),
    REFERENCE_IMAGES.find(r => r.status === "FAIL"),
  ].filter(Boolean);

  const fewShotContent = [];
  fewShotContent.push({
    type: "text",
    text: `You are a CAT heavy equipment inspection expert.
Reference labeled examples from real CAT inspections, then classify the new photo.

FIELD IDs:
Wheel Loader: 1.1 Tires/Rims, 1.2 Bucket Edge, 1.7 Transmission, 1.8 Differential, 2.1 Engine Oil, 2.2 Coolant, 2.3 Radiator, 4.1 Seat, 4.4 Alarm
Excavator: G.1 Bucket/GET, G.2 Bucket Cyl, G.3 Stick, G.4 Boom, G.5 Undercarriage, G.6 Carbody, E.1 Coolant, E.2 Engine Oil, E.3 Hydraulic, E.4 Radiator, C.1 Seat, C.4 ROPS

STATUS: FAIL=rust/cracks/damage/leaks, MONITOR=minor wear/low fluid/dirt, PASS=clean/intact/proper

Reference examples:`,
  });

  for (const ref of examples) {
    fewShotContent.push({ type: "text", text: `REFERENCE — ${ref.name} → STATUS: ${ref.status} | FIELD: ${ref.field_id}` });
    fewShotContent.push({ type: "image_url", image_url: { url: `data:image/jpeg;base64,${ref.b64}`, detail: "low" } });
  }

  fewShotContent.push({
    type: "text",
    text: `Now classify this NEW photo. Return ONLY this JSON:
{"field_id":"1.1","status":"FAIL","comment":"2-sentence professional inspection note","confidence":88}`,
  });
  fewShotContent.push({ type: "image_url", image_url: { url: `data:image/jpeg;base64,${imageBase64}`, detail: "high" } });

  const response = await openai.chat.completions.create({
    model: "gpt-4o",
    max_tokens: 200,
    messages: [{ role: "user", content: fewShotContent }],
    response_format: { type: "json_object" },
  });

  try { return JSON.parse(response.choices[0].message.content); }
  catch { return null; }
}

// ── PHOTO: Get bounding box of damage ────────────────────────────────────
export async function detectBoundingBox(imageBase64) {
  try {
    const response = await openai.chat.completions.create({
      model: "gpt-4o",
      max_tokens: 150,
      messages: [{
        role: "user",
        content: [
          {
            type: "text",
            text: `Find the most concerning damage area in this CAT equipment photo.
Return ONLY this JSON:
{"component":"hydraulic hose","condition":"cracked near coupling","x":0.35,"y":0.42,"w":0.25,"h":0.18}
x/y/w/h are fractions (0.0-1.0). x,y=top-left corner.`
          },
          { type: "image_url", image_url: { url: `data:image/jpeg;base64,${imageBase64}`, detail: "high" } }
        ]
      }],
      response_format: { type: "json_object" }
    });
    return JSON.parse(response.choices[0].message.content);
  } catch { return null; }
}

// ── ELEVENLABS: Speak finding aloud ──────────────────────────────────────
export async function speakFinding(comment) {
  const key = process.env.REACT_APP_ELEVENLABS_KEY;
  if (!key) return;
  const voiceId = "21m00Tcm4TlvDq8ikWAM"; // Rachel
  try {
    const response = await fetch(`https://api.elevenlabs.io/v1/text-to-speech/${voiceId}`, {
      method: "POST",
      headers: { "xi-api-key": key, "Content-Type": "application/json" },
      body: JSON.stringify({ text: comment, model_id: "eleven_monolingual_v1", voice_settings: { stability: 0.5, similarity_boost: 0.75 } }),
    });
    const blob = await response.blob();
    new Audio(URL.createObjectURL(blob)).play();
  } catch (e) { console.warn("ElevenLabs failed:", e); }
}

// ── DALL-E: Damage diagram ────────────────────────────────────────────────
export async function generateDamageReport(fields) {
  const fails = fields.filter(f => f.status === "FAIL").map(f => f.label).join(", ");
  const monitors = fields.filter(f => f.status === "MONITOR").map(f => f.label).join(", ");
  const response = await openai.images.generate({
    model: "dall-e-3",
    prompt: `Technical CAT equipment diagram. RED highlights: ${fails || "none"}. YELLOW highlights: ${monitors || "none"}. Dark blueprint aesthetic, CAT yellow accents.`,
    size: "1024x1024", quality: "standard",
  });
  return response.data[0].url;
}

// ── WORDBANK enrichment ───────────────────────────────────────────────────
export function enrichWithWordbank(text) {
  return { terms: matchWordbank(text), smells: matchSmells(text) };
}